import cn from 'classnames';

import RemoteIconV2 from '@/components/Icon/RemoteIconV2';
import {InlineNotification} from '@/components/ui/InlineNotification';
import {PENDING_RETURN_ACTION_IDS} from '@/consts/pendingReturnActionIds';
import useConfigExternalLink from '@/hooks/externalLink/useConfigExternalLink';
import usePendingReturnActionRedirect from '@/hooks/usePendingReturnActionRedirect';
import appContent from '@/services/content/appContent.typed';
import routerService from '@/services/route/routerService';
import type {InlineNotificationModel} from '@/types/aem/inlineNotificationModel';
import type {AutoDeliverySkipDeliveryContent} from '@/types/content/autoDelivery';
import AutoDeliveryDcxInfoBoxActionButton from '@/views/AutoDeliveryDcx/AutoDeliveryDcxInfoBoxActionButton';

import {useAutoDeliveryDcxContext} from '../../AutoDeliveryDcxContext';
import useSkipDelivery from '../hooks/useSkipDelivery';

const getSkipNotification = (
    availableSkipCount: number,
    content: Pick<
        AutoDeliverySkipDeliveryContent,
        'inlineNotification' | 'oneSkipAvailableInlineNotification' | 'twoSkipsAvailableInlineNotification'
    >
): InlineNotificationModel | undefined => {
    if (availableSkipCount === 1) return content.oneSkipAvailableInlineNotification;
    if (availableSkipCount === 2) return content.twoSkipsAvailableInlineNotification;

    return content.inlineNotification;
};

const SkipDeliveryInlineNotification = () => {
    const {setRescheduleOpenTrigger} = useAutoDeliveryDcxContext();
    const {
        autoDelivery: {availableSkipCount, isSkipDeliveryAvailable},
    } = useSkipDelivery();

    const skipDeliveryContent = appContent.getAutoDeliveryContent()?.skipDeliveryContent;

    const isSkipUnavailable = !isSkipDeliveryAvailable;
    const notification = isSkipUnavailable
        ? skipDeliveryContent?.skipUnavailableInlineNotification
        : skipDeliveryContent
          ? getSkipNotification(availableSkipCount, skipDeliveryContent)
          : undefined;

    const {redirectToExternalLink} = useConfigExternalLink(notification?.actionButton?.externalLink);
    const redirectWithPendingAction = usePendingReturnActionRedirect(
        PENDING_RETURN_ACTION_IDS.AUTO_DELIVERY_DETAILS_EXTENDED,
        redirectToExternalLink
    );

    if (!skipDeliveryContent || !notification) return null;

    const {title, description, icon, actionButton} = notification;
    const hasExternalLink = !!actionButton?.externalLink;

    const handleClick = () => {
        if (isSkipUnavailable && !hasExternalLink) {
            setRescheduleOpenTrigger(true);
            routerService.goBack();

            return;
        }

        if (hasExternalLink) {
            redirectWithPendingAction();
        }
    };

    return (
        <InlineNotification
            title={title && <p>{title}</p>}
            body={
                description && (
                    <p className={cn(!actionButton && !icon ? 'ml:text-16 ml:leading-15' : undefined)}>{description}</p>
                )
            }
            iconWrapperClassName='p-0'
            className='ml:bg-romance'
            icon={icon && <RemoteIconV2 className='size-12' icon={icon} />}
        >
            {actionButton && (
                <AutoDeliveryDcxInfoBoxActionButton
                    label={actionButton.text}
                    alt={actionButton.alt}
                    icon={actionButton.icon ? <RemoteIconV2 className='size-6' icon={actionButton.icon} /> : undefined}
                    onClick={handleClick}
                />
            )}
        </InlineNotification>
    );
};

export default SkipDeliveryInlineNotification;
